import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $createTextNode,
  $getSelection,
  $setSelection,
  $createRangeSelection,
  $getNodeByKey,
  $isRangeSelection,
  KEY_DOWN_COMMAND,
  PASTE_COMMAND,
  COMMAND_PRIORITY_LOW
} from 'lexical';
import { mergeRegister } from '@lexical/utils';

import { termTranslationShape } from '../../../propsShape';
import { TermTranslationNode } from '../../molecules';

import { Trie } from '../../../utils';
import {
  getTextOffsetInParagraph,
  findPositionInParagraph,
  ensureTextNodesAroundSpecialNodes,
  areSpecialNodesLogicallyAdjacent
} from '../../../utils/lexicalUtils';

function transformTextNodesToTermTranslations(editor, terms, skipActiveTyping = false) {
  editor.update(
    () => {
      // Save cursor position for restoration after transformation
      const selection = $getSelection();
      let savedCursor = null;
      let activelyTypingAtTermEnd = false;

      if (selection && $isRangeSelection(selection)) {
        const { anchor } = selection;
        const { focus } = selection;

        // Detect if user is actively typing at end of potential term to avoid interruption
        if (skipActiveTyping) {
          let textNode = null;
          let cursorPosition = anchor.offset;

          // Find the actual text node containing the cursor
          if (anchor.getNode().getType() === 'text') {
            textNode = anchor.getNode();
          } else if (anchor.getNode().getType() === 'paragraph') {
            // Handle paragraph-level cursor positioning
            const paragraph = anchor.getNode();
            const children = paragraph.getChildren();
            let textOffset = 0;

            for (let i = 0; i < children.length; i += 1) {
              const child = children[i];
              const childLength = child.getTextContent().length;

              if (textOffset + childLength >= anchor.offset) {
                if (child.getType() === 'text') {
                  textNode = child;
                  cursorPosition = anchor.offset - textOffset;
                  break;
                } else if (child.getType() === 'term') {
                  // Check if user was typing at end of existing term
                  const relativeOffset = anchor.offset - textOffset;
                  if (relativeOffset >= childLength) {
                    const termText = child.getTextContent();

                    // Verify this is a known term to avoid disrupting typing
                    const isKnownTerm = terms.some(({ termList }) =>
                      termList.some((term) => term.toLowerCase() === termText.toLowerCase())
                    );

                    if (isKnownTerm) {
                      activelyTypingAtTermEnd = true;
                    }
                  }
                  break;
                }
              }
              textOffset += childLength;
            }
          }

          // Check if user is typing at end of text that could become a term
          if (textNode && !activelyTypingAtTermEnd) {
            const textContent = textNode.getTextContent();

            if (cursorPosition === textContent.length && textContent.trim().length > 0) {
              const words = textContent.split(/\s+/);
              const lastWord = words[words.length - 1];

              if (lastWord && lastWord.length > 0) {
                // Check if current word could potentially match a term
                const potentiallyMatches = terms.some(({ termList }) =>
                  termList.some((term) => {
                    const termLower = term.toLowerCase();
                    const lastWordLower = lastWord.toLowerCase();
                    return termLower.startsWith(lastWordLower) || termLower === lastWordLower;
                  })
                );

                if (potentiallyMatches) {
                  activelyTypingAtTermEnd = true;
                }
              }
            }
          }
        }

        // Calculate cursor position for restoration
        const anchorNode = anchor.getNode();
        const focusNode = focus.getNode();

        let anchorParagraph = anchorNode;
        while (anchorParagraph && anchorParagraph.getType() !== 'paragraph') {
          anchorParagraph = anchorParagraph.getParent();
        }

        let focusParagraph = focusNode;
        while (focusParagraph && focusParagraph.getType() !== 'paragraph') {
          focusParagraph = focusParagraph.getParent();
        }

        if (anchorParagraph && focusParagraph) {
          const anchorTextOffset = getTextOffsetInParagraph(
            anchorParagraph,
            anchorNode,
            anchor.offset
          );
          const focusTextOffset = getTextOffsetInParagraph(focusParagraph, focusNode, focus.offset);

          savedCursor = {
            anchorParagraphKey: anchorParagraph.getKey(),
            focusParagraphKey: focusParagraph.getKey(),
            anchorTextOffset,
            focusTextOffset
          };
        }
      }

      // Skip transformation if user is actively typing
      if (activelyTypingAtTermEnd) {
        return;
      }

      const root = $getRoot();
      const paragraphNodes = root.getChildren().filter((node) => node.getType() === 'paragraph');

      paragraphNodes.forEach((paragraphNode) => {
        const children = paragraphNode.getChildren();
        let previousTextNode = null;
        let newChildren = [];

        // PASS 1: Convert adjacent special nodes to text for merging
        children.forEach((node, index) => {
          const nodeType = node.getType();
          const nodeText = node.getTextContent();

          if (nodeType === 'term') {
            // Check for adjacency to other special nodes
            const hasAdjacentEntityNode = areSpecialNodesLogicallyAdjacent(
              children,
              index,
              'entity'
            );
            const hasAdjacentTermNode = areSpecialNodesLogicallyAdjacent(children, index, 'term');

            // Check for direct text adjacency (user deleted space)
            const isDirectlyAdjacentToText = () => {
              const prevSibling = index > 0 ? children[index - 1] : null;
              const nextSibling = index < children.length - 1 ? children[index + 1] : null;

              // Detect text nodes without separating whitespace
              const adjacentToPrevText =
                prevSibling &&
                prevSibling.getType() === 'text' &&
                prevSibling.getTextContent().length > 0 &&
                !/\s$/.test(prevSibling.getTextContent());

              const adjacentToNextText =
                nextSibling &&
                nextSibling.getType() === 'text' &&
                nextSibling.getTextContent().length > 0 &&
                !/^\s/.test(nextSibling.getTextContent());

              return adjacentToPrevText || adjacentToNextText;
            };

            const isAdjacentToText = isDirectlyAdjacentToText();

            // Convert term to text if adjacent for re-evaluation
            if (hasAdjacentEntityNode || hasAdjacentTermNode || isAdjacentToText) {
              const textNode = $createTextNode(nodeText);

              // Merge with previous text node if exists
              if (previousTextNode) {
                const prevText = previousTextNode.getTextContent();
                const newText = textNode.getTextContent();
                const mergedText = prevText + newText; // Respect user's spacing choice

                previousTextNode.setTextContent(mergedText);
              } else {
                newChildren.push(textNode);
                previousTextNode = textNode;
              }
            } else {
              // Keep standalone term node
              newChildren.push(node);
              previousTextNode = null;
            }
          } else if (nodeType === 'entity') {
            // Convert entity to text if adjacent to terms
            const hasAdjacentTermNode = areSpecialNodesLogicallyAdjacent(children, index, 'term');

            if (hasAdjacentTermNode) {
              const textNode = $createTextNode(nodeText);
              if (previousTextNode) {
                const prevText = previousTextNode.getTextContent();
                const newText = textNode.getTextContent();
                const mergedText = prevText + newText; // Direct merge

                previousTextNode.setTextContent(mergedText);
              } else {
                newChildren.push(textNode);
                previousTextNode = textNode;
              }
            } else {
              // Keep standalone entity node
              newChildren.push(node);
              previousTextNode = null;
            }
          } else if (nodeType === 'text') {
            // Merge consecutive text nodes
            if (previousTextNode) {
              const prevText = previousTextNode.getTextContent();
              const newText = nodeText;
              const mergedText = prevText + newText;

              previousTextNode.setTextContent(mergedText);
            } else {
              newChildren.push(node);
              previousTextNode = node;
            }
          } else {
            // Keep other node types as-is
            newChildren.push(node);
            previousTextNode = null;
          }
        });

        // PASS 2: Transform text nodes to term nodes where matches found
        newChildren = newChildren.flatMap((node) => {
          if (node.getType() !== 'text') {
            return node;
          }

          const nodeText = node.getTextContent();

          // Find exact term matches using word boundaries
          const exactMatches = [];
          terms.forEach(({ termList, displayedDescription }) => {
            termList
              .sort((a, b) => b.length - a.length) // Prioritize longer terms
              .forEach((term) => {
                const standardRegex = new RegExp(
                  `\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
                  'gi'
                );
                const matches = nodeText.match(standardRegex);

                if (matches && matches.length > 0) {
                  matches.forEach((match) => {
                    exactMatches.push({ term, match, definition: displayedDescription });
                  });
                }
              });
          });

          if (exactMatches.length === 0) {
            return node;
          }

          // Use Trie for efficient text processing
          const trie = new Trie();
          exactMatches.forEach(({ match }) => {
            trie.insert(match);
          });

          let result = '';
          let i = 0;
          const text = nodeText;
          const newNodes = [];

          // Process text character by character, creating term nodes for matches
          while (i < text.length) {
            const matchEnd = trie.searchInText(text, i);

            if (matchEnd !== -1) {
              const slice = text.slice(i, matchEnd);
              const matchInfo = exactMatches.find(
                ({ match }) => match.toLowerCase() === slice.toLowerCase()
              );

              // Add any text before the term
              if (result.length > 0) {
                newNodes.push($createTextNode(result));
              }

              // Create term node
              newNodes.push(new TermTranslationNode(slice, matchInfo?.definition || ''));
              result = '';
              i = matchEnd;
            } else {
              result += text[i];
              i += 1;
            }
          }

          // Add any remaining text
          if (result.length > 0) {
            newNodes.push($createTextNode(result));
          }

          return newNodes;
        });

        // Replace paragraph children with processed nodes
        paragraphNode.getChildren().forEach((child) => child.remove());
        paragraphNode.append(...newChildren);

        // Add text nodes around terms for proper cursor positioning
        const finalChildren = paragraphNode.getChildren();
        const hasTermNodes = finalChildren.some((child) => child.getType() === 'term');

        if (hasTermNodes) {
          ensureTextNodesAroundSpecialNodes(paragraphNode, 'term');
        }
      });

      // Restore cursor position after transformation
      if (savedCursor) {
        const { anchorParagraphKey, focusParagraphKey, anchorTextOffset, focusTextOffset } =
          savedCursor;

        const anchorParagraph = $getNodeByKey(anchorParagraphKey);
        const focusParagraph = $getNodeByKey(focusParagraphKey);

        if (anchorParagraph && focusParagraph) {
          const anchorPoint = findPositionInParagraph(anchorParagraph, anchorTextOffset);
          const focusPoint = findPositionInParagraph(focusParagraph, focusTextOffset);

          if (anchorPoint && focusPoint) {
            const newSelection = $createRangeSelection();
            newSelection.anchor.set(anchorPoint.node.getKey(), anchorPoint.offset, 'text');
            newSelection.focus.set(focusPoint.node.getKey(), focusPoint.offset, 'text');
            $setSelection(newSelection);
          } else if (!anchorPoint && anchorParagraph) {
            anchorParagraph.selectEnd();
          } else if (!focusPoint && focusParagraph) {
            focusParagraph.selectEnd();
          }
        }
      }
    },
    { discrete: true }
  );
}

const TermHighlightPlugin = ({ terms }) => {
  const [editor] = useLexicalComposerContext();
  const transformationTimeoutRef = useRef(null);
  const lastContentHashRef = useRef('');
  const transformationCountRef = useRef(0);

  // Helper to create content hash for loop detection
  const getContentHash = () => {
    let hash = '';
    editor.getEditorState().read(() => {
      const root = $getRoot();
      const paragraphs = root.getChildren().filter((node) => node.getType() === 'paragraph');

      paragraphs.forEach((paragraph) => {
        const children = paragraph.getChildren();
        hash += children.map((child) => `${child.getType()}:${child.getTextContent()}`).join('|');
      });
    });
    return hash;
  };

  function performTransformation(delay = 0, skipActiveTyping = false) {
    if (transformationTimeoutRef.current) {
      clearTimeout(transformationTimeoutRef.current);
    }

    transformationTimeoutRef.current = setTimeout(() => {
      // Check for transformation loops
      const currentHash = getContentHash();

      if (currentHash === lastContentHashRef.current) {
        transformationTimeoutRef.current = null;
        transformationCountRef.current = 0;
        return;
      }

      // Reset transformation count if content changed significantly
      if (Math.abs(currentHash.length - lastContentHashRef.current.length) > 10) {
        transformationCountRef.current = 0;
      }

      transformationCountRef.current += 1;

      // Prevent excessive transformations
      if (transformationCountRef.current > 3) {
        transformationTimeoutRef.current = null;
        transformationCountRef.current = 0;
        return;
      }

      transformTextNodesToTermTranslations(editor, terms, skipActiveTyping);
      lastContentHashRef.current = getContentHash();
      transformationTimeoutRef.current = null;

      // Reset count after successful transformation
      setTimeout(() => {
        transformationCountRef.current = 0;
      }, 1000);
    }, delay);
  }

  // Handle terms changes with debouncing
  useEffect(() => {
    performTransformation(100);
  }, [editor, terms]);

  useEffect(
    () =>
      mergeRegister(
        editor.registerCommand(
          KEY_DOWN_COMMAND,
          (event) => {
            if (event.metaKey || event.ctrlKey) return false;
            if (/^[a-zA-Z0-9\s]$/.test(event.key) || event.key === 'Backspace') {
              if (event.key === 'Backspace') {
                performTransformation(300, false);
              } else if (/^[a-zA-Z0-9]$/.test(event.key)) {
                performTransformation(800, true);
              } else {
                performTransformation(100, false);
              }
            }
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          PASTE_COMMAND,
          () => {
            performTransformation(100);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, terms]
  );

  return null;
};

TermHighlightPlugin.propTypes = {
  terms: PropTypes.arrayOf(termTranslationShape).isRequired
};

export { TermHighlightPlugin };
